import { useState, type ReactNode } from 'react'
import { Box, Button, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material'
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined'
import type { Usuario } from '../../api'
import { DialogCashback } from '../../components/dialog-cashback'
import { MINIMO_A_PAGAR_ONLINE } from '../../lib/cashback-pedido'
import { formatarBRL, formatarPercentual } from '../../lib/formato'
import type { CashbackNoPedido, MotivoDeNaoUsarCashback } from './cashback-no-checkout'

const TEXTO_DO_MOTIVO: Record<MotivoDeNaoUsarCashback, string> = {
  PAGAMENTO_ONLINE: `No pagamento online, precisa sobrar pelo menos ${formatarBRL(MINIMO_A_PAGAR_ONLINE)} para pagar. Escolha outra forma de pagamento ou coloque mais itens no pedido para usar seu cashback.`,
  VALOR_DO_PEDIDO: 'Não dá para usar o cashback com o valor deste pedido. Coloque mais itens no pedido para usar seu saldo.',
}

interface BlocoCashbackProps {
  cashback: CashbackNoPedido
  /** Perfil do cliente logado: preenche o cadastro no cashback. Ausente sem login e enquanto o perfil carrega. */
  usuario: Pick<Usuario, 'nome' | 'cpf' | 'telefone'> | undefined
  aoEntrar: () => void
  aoLigarOuDesligar: (ligado: boolean) => void
}

/**
 * Cashback no passo "Pagamento" do delivery, entre o cupom e a forma de pagamento: é a ordem em
 * que o valor é calculado. Só mostra: quem decide a situação e os valores é `cashback-no-checkout.ts`.
 */
export function BlocoCashback({ cashback, usuario, aoEntrar, aoLigarOuDesligar }: BlocoCashbackProps): JSX.Element | null {
  const [cadastroAberto, setCadastroAberto] = useState(false)

  // O diálogo fica fora das condições do bloco: o cadastro refaz a consulta do saldo até quando falha,
  // o bloco pode trocar de conteúdo ou sumir, e a mensagem do cadastro ainda está na tela.
  const dialogoDeCadastro =
    cadastroAberto && usuario ? <DialogCashback usuario={usuario} aoFechar={() => setCadastroAberto(false)} /> : null

  if (cashback.situacao === 'OCULTO') return dialogoDeCadastro

  // O cadastro nasce do perfil: sem ele carregado, o convite ainda não tem o que abrir.
  if (cashback.situacao === 'CONVITE_DE_CADASTRO' && !usuario) return dialogoDeCadastro

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <RedeemOutlinedIcon color="action" />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {cashback.situacao === 'CONVITE_PARA_ENTRAR' && (
              <Convite texto="Tem cashback nesta loja? Entre para usar neste pedido." botao="Entrar" aoTocar={aoEntrar} />
            )}
            {cashback.situacao === 'CONVITE_DE_CADASTRO' && (
              <Convite
                texto="Já ganhou cashback nesta loja? Cadastre-se para usar neste pedido."
                botao="Cadastrar"
                aoTocar={() => setCadastroAberto(true)}
              />
            )}
            {cashback.situacao === 'SALDO_ZERO' && (
              <>
                <Typography>Saldo atual de cashback: {formatarBRL(0)}</Typography>
                {/* Não promete "ganhe neste pedido": dar cashback é outro switch da loja. */}
                <Apoio>Quando você ganhar cashback nesta loja, o valor aparece aqui.</Apoio>
              </>
            )}
            {cashback.situacao === 'COM_SALDO' && <UsarCashback cashback={cashback} aoLigarOuDesligar={aoLigarOuDesligar} />}
          </Box>
        </Stack>
      </Paper>
      {dialogoDeCadastro}
    </>
  )
}

function Convite({ texto, botao, aoTocar }: { texto: string; botao: string; aoTocar: () => void }): JSX.Element {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Typography sx={{ flexGrow: 1 }}>{texto}</Typography>
      <Button size="small" variant="outlined" onClick={aoTocar} sx={{ flexShrink: 0 }}>
        {botao}
      </Button>
    </Stack>
  )
}

interface UsarCashbackProps {
  cashback: CashbackNoPedido
  aoLigarOuDesligar: (ligado: boolean) => void
}

function UsarCashback({ cashback, aoLigarOuDesligar }: UsarCashbackProps): JSX.Element {
  const daParaUsar = cashback.maximo > 0
  const ligado = cashback.valorUsado > 0
  const rotulo = daParaUsar ? `Usar ${formatarBRL(cashback.maximo)} de cashback` : 'Usar cashback'

  const lojaTemTeto = cashback.percentualMaxUso > 0
  const textoDoSaldo = `Saldo: ${formatarBRL(cashback.saldo)}.`
  const textoDoTeto = `Nesta loja, o cashback paga até ${formatarPercentual(cashback.percentualMaxUso)} do valor dos itens.`

  return (
    <>
      <FormControlLabel
        labelPlacement="start"
        control={<Switch checked={ligado} disabled={!daParaUsar} onChange={(_evento, novoValor) => aoLigarOuDesligar(novoValor)} />}
        label={<Typography fontWeight={600}>{rotulo}</Typography>}
        sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
      />
      {/* Com tentativa pendente o saldo de agora pode já ter descontado esse valor, e a linha contradiria o switch. */}
      {!cashback.daTentativaPendente && <Apoio>{lojaTemTeto ? `${textoDoSaldo} ${textoDoTeto}` : textoDoSaldo}</Apoio>}
      {/* Sem poder usar, o switch fica na tela, desligado e travado: se sumisse, o cliente acharia que perdeu o saldo. */}
      {cashback.motivoDeNaoUsar && <Apoio>{TEXTO_DO_MOTIVO[cashback.motivoDeNaoUsar]}</Apoio>}
    </>
  )
}

function Apoio({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  )
}
